import { ClientOnly } from 'components/ClientOnly'
import { ConnectedShop } from 'components/Phantom/_pageCompositions/Shop'
import { FeaturedReviews } from 'components/Phantom/_sections/FeaturedReviews'
import { NewStandardConnected } from 'components/Phantom/_sections/NewStandard'
import { PersonalSleepConnected } from 'components/Phantom/_sections/PersonalSleep'
import { ScrollSequenceStatic } from 'components/Phantom/_sections/ScrollSequenceStatic'
import { SleepElixirVanityAdConnected } from 'components/SleepElixirVanityAd'
import { VanityFooter } from 'components/VanityFooter'
import { VanityHero } from 'components/VanityHero'
import { AdtPixel } from 'components/_pixels'
import { SiteSettings } from 'config/types'
import { amViewedShopSectionOnVanity } from 'events/amplitude'
import { Awards } from 'pageCores/homepage/components/Awards'
import { BetterSleepDrawers, PersonalizedSleep } from 'pageCores/prospectingCMin/localComponents'
import { PriceManager } from 'prices'
import { FC, useCallback, useEffect, useState } from 'react'
import { Waypoint } from 'react-waypoint'
import { useStore } from 'stores/StoreProvider'
import { useCurrency, useGetUpToDiscountNumber, usePod4UltraPrices } from 'stores/prices'
import { useCurrentRegion, useMetricRegion } from 'stores/settings'
import { trackViewShopPage, useSelectedVariant } from 'stores/shop'

interface Props {
	settings: SiteSettings
}

export const Vanity: FC<Props> = (props) => {
	const { settings } = props
	const metricRegion = useMetricRegion()
	const setShowShopBanner = useStore((state) => state.shop.setShowShopBanner)

	const currency = useCurrency()
	const getUpToDiscountNumber = useGetUpToDiscountNumber()
	const discountFormatted = PriceManager.formatPriceToCurrencyNoDecimal(getUpToDiscountNumber, currency)

	const [viewed, setViewed] = useState(false)

	useEffect(() => {
		setShowShopBanner(true)
	}, [])

	const pod4UltraPrices = usePod4UltraPrices()
	const model = useStore((state) => state.shop.selectedSelections['pod-model'])
	const selectedVariant = useSelectedVariant()
	const currentRegion = useCurrentRegion()
	const globalPromoCode = useStore((state) => state.settings.globalPromoCode)
	const podData = useStore((state) => state.shop.podData)

	const waypointOnEnter = useCallback(() => {
		if (viewed) return
		trackViewShopPage({
			pod4UltraPrices,
			model,
			selectedVariant,
			currentRegion,
			globalPromoCode,
			podData,
		})
		amViewedShopSectionOnVanity(settings.partnerName)
		setViewed(true)
	}, [viewed, pod4UltraPrices, model, selectedVariant, currentRegion, globalPromoCode, podData, settings.partnerName])

	return (
		<>
			<VanityHero
				title={settings.heroSettings?.heading || `${settings.partnerName} sleeps on the Pod`}
				author={settings.heroSettings?.description}
				quote={settings.homeSettings.heroTitle}
				boldQuote
				phantomImage={settings.phantomImage}
				bgImg={settings.heroSettings?.bgImage}
				sources={settings.heroSettings?.sources}
				alt={settings.emailCapSettings?.imageAlt || ''}
				ctaLabel={`Shop now`}
				className="outer-margins-bottom"
				discount={discountFormatted}
				metric={metricRegion}
				partnerName={settings.partnerName}
			/>
			<NewStandardConnected />
			<div>
				<Waypoint
					onEnter={waypointOnEnter}
					scrollableAncestor={'window'}
				/>
			</div>
			<ClientOnly>
				<ConnectedShop />
			</ClientOnly>
			<SleepElixirVanityAdConnected />
			<PersonalSleepConnected
				showExtraSleep={false}
				eyebrow={'Put your sleep on Autopilot'}
				header={'Your perfect sleep conditions, all night'}
				subHeader={"The Pod personalizes your side of the bed, creating the ultimate environment for deep, restorative sleep—even if you and your partner can't agree on the perfect temperature."}
				cta={{
					href: '#shop',
					text: 'Shop now',
				}}
				theme={'white'}
			/>
			<PersonalizedSleep theme={'light'} />
			{/*<SplitFeatureFinancing />*/}
			<BetterSleepDrawers theme={'white'} />
			<FeaturedReviews />
			<VanityFooter
				title={settings.preFooterSettings?.title || `${settings.partnerName} sleeps on the Pod`}
				quote={settings.preFooterSettings?.paragraphs?.[0] ?? `${settings.partnerName} swears by the Pod cover to get better sleep. Learn more for yourself.`}
				phantomImage={settings.phantomImage}
				bgImg={settings.heroSettings?.bgImage}
				sources={settings.preFooterSettings?.sources ?? settings.heroSettings?.sources}
				alt={settings.emailCapSettings?.imageAlt || ''}
				ctaLabel={`Get ${discountFormatted} off the Pod`}
				subtitle="The Pod"
				subheadline={`Unlock ${discountFormatted} off`}
				metric={metricRegion}
			/>
			<ScrollSequenceStatic title={`Shop Pod 4 Ultra in our ${settings.saleName}`} />
			<Awards />
			<AdtPixel id="6563" />
		</>
	)
}
